import { useCallback } from "react";
import { toast } from "react-toastify";
import VLToastComponent from "views/components/VLToastComponent";
import {
  fetchFlywheelRunCycleApiV1FlywheelDatasetIdCycleFlowIdGet,
  useFlywheelApplyResultsApiV1FlywheelDatasetIdApplyFlowIdPut,
  useFlywheelRunAbortApiV1FlywheelDatasetIdAbortFlowIdDelete,
} from "../../vl-api/vlComponents";
import { useRefetchFlywheelStatus } from "./useFlywheelStatus";
import { useUserConfig } from "../useUserConfig";

export const useFlywheelCycle = ({
  onAborted,
  onFinished,
}: {
  onAborted: () => void;
  onFinished: () => void;
}) => {
  const updateFlywheelStatus = useRefetchFlywheelStatus();
  const { refetch: refetchUserConfig } = useUserConfig();
  const runCycle = useCallback(
    async (datasetId: string, flowId: string) => {
      try {
        await fetchFlywheelRunCycleApiV1FlywheelDatasetIdCycleFlowIdGet({
          pathParams: { datasetId, flowId },
        });
        updateFlywheelStatus("RESUME_FLOW");
      } catch (err) {
        console.error("Failed to run flywheel cycle", err);
        throw err;
      }
    },
    [updateFlywheelStatus],
  );

  const handleError = (title: string, err: any) => {
    console.error(err);
    const toastId = toast(
      <VLToastComponent
        title={title}
        status="ERROR"
        description={`Something went wrong ${err.detail}`}
        actionButton={{
          text: "Close",
          type: "primary",
          onClick: () => toast.dismiss(toastId),
        }}
        onClose={() => toast.dismiss(toastId)}
      />,
      { autoClose: 7000, closeButton: false, closeOnClick: false },
    );
  };

  const finishCycleMutation =
    useFlywheelApplyResultsApiV1FlywheelDatasetIdApplyFlowIdPut({
      onSuccess: (data) => {
        // with the final review enabled, Finish stops the flow at FINAL_REVIEW
        updateFlywheelStatus(data?.status ?? "APPLY_MODEL_TO_DATASET");
        onFinished();
      },
      onError: (err) => handleError("Finish", err),
    });

  const resetCycleMutation =
    useFlywheelRunAbortApiV1FlywheelDatasetIdAbortFlowIdDelete({
      onSuccess: async () => {
        updateFlywheelStatus("ABORTED");
        await refetchUserConfig();
        onAborted();
      },
      onError: (err) => handleError("Abort", err),
    });

  const finishCycle = useCallback(
    (datasetId: string, flowId: string) =>
      finishCycleMutation.mutate({ pathParams: { datasetId, flowId } }),
    [finishCycleMutation],
  );

  const resetCycle = useCallback(
    (datasetId: string, flowId: string) =>
      resetCycleMutation.mutate({ pathParams: { datasetId, flowId } }),
    [resetCycleMutation],
  );

  return { runCycle, finishCycle, resetCycle };
};
